import {
  AddIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  FormInput,
  FormSelect,
  SecondaryButton,
  toast,
} from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { isCancel } from 'axios'
import CourseCurriculumEmptyList from 'modules/courses/courses/curriculum/components/course-curriculum-empty-list'
import CourseCurriculumSkeleton from 'modules/courses/courses/curriculum/components/course-curriculum-skeleton'
import CourseCurriculumTable, {
  CourseCurriculumTableProps,
} from 'modules/courses/courses/curriculum/components/course-curriculum-table'
import CreateModuleModal from 'modules/courses/courses/curriculum/components/create-module-modal'
import { useCourse } from 'modules/courses/courses/hooks/use-course'
import ItemSteps from 'modules/stores/store/components/item-steps'
import { StoreTextEditor } from 'modules/stores/store/components/store-text-editor'
import {
  ChangeEventHandler,
  FocusEventHandler,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { getStateStatusIcon } from 'shared/utils/get-status-icon'
import { LectureId } from '../../curriculum/types/lecture-interface'
import { CreateModuleInterface } from '../../curriculum/types/module-interface'
import { CourseId } from '../../types/course-interface'
import { LightCourseLectureEditor } from '../components/light-course-lecture-editor'
import { useLightCourseActions } from '../hooks/use-light-course-actions'
import { useLightCourseCurriculum } from '../hooks/use-light-course-curriculum'
import { useLightCoursesListAll } from '../hooks/use-light-courses-list-all'
import { getDefaultLightCourseContent } from '../light-course-default-content'
import { twMerge } from 'tailwind-merge'

export interface LightCourseEditorProps {
  domainId?: number
  courseId?: number
  courseSelectorError?: string
  backButton?: ReactNode
  onChangeCourse: (courseId?: number) => void
  onToggleLectureEditor?: (lectureId?: number) => void
}

export const LightCourseEditor = ({
  domainId,
  courseId: passedCourseId,
  courseSelectorError,
  backButton,
  onChangeCourse,
  onToggleLectureEditor,
}: LightCourseEditorProps) => {
  const { t } = useLocoTranslation()
  const timers = useRef<Record<string, NodeJS.Timeout>>({})
  const [isCreate, setIsCreate] = useState<boolean>(false)
  const [isPatchNameSuccessful, setIsPatchNameSuccessful] = useState<
    true | undefined | null | string
  >()
  const [isPatchDescriptionSuccessful, setIsPatchDescriptionSuccessful] = useState<
    true | undefined | null | string
  >()

  const [isCourseCreating, setIsCourseCreating] = useState<boolean>(false)
  const [name, setName] = useState<string>()
  const [description, setDescription] = useState<string | null>()
  const [lectureId, setLectureId] = useState<number>()

  const {
    patchLightCourseOptions,
    createLightCourseModule,
    createLightCourseLectureWithDefaultValues,
    createLightCourseWithDefaultValues,
  } = useLightCourseActions()

  const { coursesData, mutate: mutateCoursesListAll } = useLightCoursesListAll()

  const courseId = coursesData?.find(course => course.id === passedCourseId)?.id
  const { courseData, mutate: mutateCourse } = useCourse(courseId)
  const { data: courseCurriculumData, mutate: mutateCurriculum } =
    useLightCourseCurriculum(courseId)
  const { revalidateModules } = useRevalidateModules()

  useEffect(() => {
    if (!courseData) {
      if (isCreate) {
        setName(getDefaultLightCourseContent().name)
        setDescription(getDefaultLightCourseContent().description)
      }
      return
    }
    setName(courseData.name)
    setDescription(courseData.description)
  }, [courseData?.id, isCreate])

  useEffect(() => {
    onToggleLectureEditor?.(lectureId)
  }, [lectureId])

  const handleNameChange: ChangeEventHandler<HTMLInputElement> = async event => {
    setName(event.target.value)
  }

  const handleDescriptionChange = async (description: string) => {
    setDescription(description)
  }

  const handleNameBlur: FocusEventHandler<HTMLInputElement> = async event => {
    try {
      if (!courseId || courseData?.name === event.target.value) {
        return
      }

      setIsPatchNameSuccessful(null)
      clearTimeout(timers.current['patchCourseName'])

      await patchLightCourseOptions(courseId, { name: event.target.value }, 'patchCourseName')
      await mutateCourse(data => {
        if (!data) return
        return {
          ...data,
          name: event.target.value,
        }
      }, false)
      await mutateCoursesListAll(data => {
        if (!data) return
        return {
          ...data,
          items: data.items.map(course => {
            if (course.id !== courseId) return course
            return {
              ...course,
              name: event.target.value,
            }
          }),
        }
      }, false)
      await revalidateModules([RequestModuleEnum.MembershipLightCourse])
      setIsPatchNameSuccessful(true)
      timers.current['patchCourseName'] = setTimeout(() => {
        setIsPatchNameSuccessful(undefined)
      }, 5_000)
    } catch (e) {
      if (isCancel(e)) {
        return
      }
      if (e instanceof BadRequest) {
        setIsPatchNameSuccessful(e.errors.fields.name.toString() || t('global.error'))
      } else {
        setIsPatchNameSuccessful(t('global.error'))
      }
    }
  }

  const handleDescriptionBlur = async (description: string) => {
    try {
      if (!courseId || courseData?.description === description) {
        return
      }

      setIsPatchDescriptionSuccessful(null)
      clearTimeout(timers.current['patchCourseDescription'])

      await patchLightCourseOptions(courseId, { description }, 'patchCourseDescription')
      await mutateCourse(data => {
        if (!data) return

        return {
          ...data,
          description,
        }
      }, false)
      setIsPatchDescriptionSuccessful(true)
      timers.current['patchCourseDescription'] = setTimeout(() => {
        setIsPatchDescriptionSuccessful(undefined)
      }, 5_000)
    } catch (e) {
      if (isCancel(e)) {
        return
      }
      if (e instanceof BadRequest) {
        setIsPatchDescriptionSuccessful(e.errors.fields.description.toString() || t('global.error'))
      } else {
        setIsPatchDescriptionSuccessful(t('global.error'))
      }
    }
  }

  const handleCreateNewCourse = async () => {
    try {
      setIsCourseCreating(true)
      if (domainId) {
        const { id } = await createLightCourseWithDefaultValues(domainId)
        await revalidateModules([RequestModuleEnum.MembershipLightCourse])
        onChangeCourse(id)
        setIsCreate(true)
      }
    } catch {
      toast.error(t('global.error'))
    } finally {
      setIsCourseCreating(false)
    }
  }

  const handleSelectCourse = () => {
    setIsCreate(false)
  }

  const createModuleWithMutation = async (
    courseId: CourseId,
    moduleData: CreateModuleInterface,
  ) => {
    const newModule = await createLightCourseModule(courseId, moduleData)
    await mutateCurriculum(data => {
      if (data) {
        return {
          ...data,
          modules: [...data.modules, newModule],
        }
      }
    }, false)
    await revalidateModules([RequestModuleEnum.MembershipLightCourseCurriculum])
  }

  const handleEditLecture = async (lectureId: LectureId) => {
    setLectureId(lectureId)
  }

  const handleCloseLectureEditor = () => {
    setLectureId(undefined)
  }

  const handleAddLectureClick: CourseCurriculumTableProps['onAddLectureClick'] = async (
    moduleId,
    setFetching,
  ) => {
    try {
      setFetching(true)
      const { id } = await createLightCourseLectureWithDefaultValues(moduleId)
      await mutateCurriculum()
      await handleEditLecture(id)
      await revalidateModules([RequestModuleEnum.MembershipCourseLecture])
    } catch {
      toast.error(t('global.error'))
    } finally {
      setFetching(false)
    }
  }

  const courseItems = [
    !isCreate && {
      key: 'course select',
      title: t('dashboard.store.item.e_course.select_course.title'),
      element: (
        <FormSelect
          placeholder={t('dashboard.store.item.e_course.select_course.placeholder')}
          value={courseId}
          isPreFetching={!coursesData}
          data={coursesData}
          onChange={onChangeCourse}
          error={courseSelectorError}
          withoutCloseIcon
        />
      ),
    },
    (isCreate || !!courseId) && {
      key: 'semantics',
      title: t('dashboard.light_course.write_description'),
      element: (
        <>
          <FormInput
            label={t('dashboard.light_course.course_title')}
            required
            // https://app.rollbar.com/a/systemeio/fix/item/dashboard-frontend/12435#detail
            value={name ?? ''}
            placeholder={t('dashboard.light_course.course_title')}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            isPreFetching={!courseData}
            error={
              (typeof isPatchNameSuccessful === 'string' && isPatchNameSuccessful) || undefined
            }
            success={isPatchNameSuccessful === true}
            statusIcon={getStateStatusIcon(
              isPatchNameSuccessful === null,
              isPatchNameSuccessful === true,
            )}
          />

          <StoreTextEditor
            label={t('dashboard.course.description_title')}
            required
            content={description ?? ''}
            onChange={handleDescriptionChange}
            onBlur={handleDescriptionBlur}
            isPreFetching={!courseData}
            className={twMerge(
              isPatchDescriptionSuccessful === true && 'border-green',
              typeof isPatchDescriptionSuccessful === 'string' && 'border-danger',
            )}
            error={
              (typeof isPatchDescriptionSuccessful === 'string' && isPatchDescriptionSuccessful) ||
              undefined
            }
            statusIcon={getStateStatusIcon(
              isPatchDescriptionSuccessful === null,
              isPatchDescriptionSuccessful === true,
            )}
          />
        </>
      ),
    },
    (isCreate || !!courseId) && {
      key: 'curriculum',
      title: t('dashboard.light_course.add_modules'),
      element: (() => {
        if (!courseCurriculumData || !courseId) return <CourseCurriculumSkeleton />
        if (!courseCurriculumData.modules?.length)
          return (
            <CourseCurriculumEmptyList
              onModuleCreate={createModuleWithMutation}
              courseId={courseId}
              disabled={false}
            />
          )
        return (
          <>
            <CourseCurriculumTable
              isLightCourse
              modules={courseCurriculumData.modules}
              mutate={mutateCurriculum}
              courseId={courseId}
              onAddLectureClick={handleAddLectureClick}
              onEditLecture={handleEditLecture}
              onLectureNameClick={handleEditLecture}
            />
            <div className="self-center">
              <CreateModuleModal
                onModuleCreate={createModuleWithMutation}
                courseId={courseId}
                disabled={!courseCurriculumData}
              />
            </div>
          </>
        )
      })(),
    },
  ].filter(e => e !== false) as { key: string; title: ReactNode; element: ReactNode }[]

  if (lectureId) {
    return (
      <div className="flex flex-col gap-5">
        <div className="flex flex-col gap-5 lg:flex-row lg:justify-between">
          <button
            onClick={handleCloseLectureEditor}
            className="hover:text-text-primary main-transition-colors group flex items-center gap-2 text-lg font-medium"
          >
            <div className="[&>svg>path]:fill-text-tertiary group-hover:[&>svg>path]:fill-text-primary [&>svg>path]:main-transition-colors rotate-180">
              <ArrowRightIcon />
            </div>
            {t('dashboard.light_course.go_to_course_editor')}
          </button>
        </div>

        <LightCourseLectureEditor
          courseId={courseId}
          lectureId={lectureId}
          afterSubmit={handleCloseLectureEditor}
        />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-5 lg:flex-row lg:justify-between">
        {backButton}

        {!isCreate && (
          <SecondaryButton
            type="button"
            className="text-brand-primary hover:!bg-background-secondary hover:!text-text-primary main-transition-colors group mr-auto min-h-min border-none pl-0 lg:ml-auto lg:mr-0 lg:pl-4"
            isFetching={isCourseCreating}
            onClick={handleCreateNewCourse}
          >
            <AddIcon className="fill-brand-primary group-hover:fill-text-primary main-transition-colors main-transition-colors" />
            {t('dashboard.light_course.create_new_light_course')}
          </SecondaryButton>
        )}

        {isCreate && (
          <SecondaryButton
            type="button"
            className="text-brand-primary hover:!bg-background-secondary hover:!text-text-primary main-transition-colors group mr-auto min-h-min border-none pl-0 lg:ml-auto lg:mr-0 lg:pl-4"
            onClick={handleSelectCourse}
          >
            <CheckCircleIcon className="fill-brand-primary group-hover:fill-text-primary main-transition-colors main-transition-colors" />
            {t('dashboard.light_course.choose_existing_light_course')}
          </SecondaryButton>
        )}
      </div>
      <ItemSteps steps={courseItems} />
    </div>
  )
}
